import { Link2 } from "lucide-react";
import { GATE_COPY, type JdSendGate } from "../../lib/jdSendGate";
import type { DashboardField } from "../../types/contract";

type GatedField = { field: DashboardField; gate: JdSendGate };

function UnlinkedFields({
  unlinked,
  onLink,
}: {
  unlinked: GatedField[];
  onLink: (field: DashboardField) => void;
}) {
  if (!unlinked.length) return null;
  return (
    <div className="rounded border border-line bg-canvas/40 p-2">
      <span className="block text-[11px] font-medium text-fg">
        {unlinked.length > 1
          ? GATE_COPY.unlinked.titleMany
          : GATE_COPY.unlinked.title}
      </span>
      <span className="mt-0.5 block text-[10px] leading-snug text-faint">
        {GATE_COPY.unlinked.body}
      </span>
      <ul className="mt-1 space-y-1">
        {unlinked.map(({ field }) => (
          <li
            key={field.field_id}
            className="flex items-center justify-between gap-2 text-[11px]"
          >
            <span className="truncate text-muted">{field.field_name}</span>
            <button
              onClick={() => onLink(field)}
              className="flex shrink-0 items-center gap-1 rounded bg-accent px-2 py-0.5 text-[10px] font-medium text-canvas hover:bg-accent-hover"
            >
              <Link2 size={10} /> {GATE_COPY.unlinked.action}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AmbiguousFields({
  ambiguous,
  chosen,
  jdNames,
  onChoose,
}: {
  ambiguous: GatedField[];
  chosen: Record<string, string>;
  jdNames: Record<string, string>;
  onChoose: (fieldId: string, value: string) => void;
}) {
  if (!ambiguous.length) return null;
  return (
    <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2">
      <span className="block text-[11px] font-medium text-fg">
        {ambiguous.length > 1
          ? GATE_COPY.ambiguous.titleMany
          : GATE_COPY.ambiguous.title}
      </span>
      <span className="mt-0.5 block text-[10px] leading-snug text-faint">
        {GATE_COPY.ambiguous.body}
      </span>
      <ul className="mt-1 space-y-1">
        {ambiguous.map(({ field, gate }) => (
          <li
            key={field.field_id}
            className="flex items-center justify-between gap-2 text-[11px]"
          >
            <span className="truncate text-muted">{field.field_name}</span>
            <select
              aria-label={`John Deere field for ${field.field_name}`}
              className="rounded border border-line bg-surface px-1 py-0.5 text-[11px] text-fg"
              value={chosen[field.field_id] ?? ""}
              onChange={(e) => onChoose(field.field_id, e.target.value)}
            >
              <option value="" disabled>
                {GATE_COPY.ambiguous.action}…
              </option>
              {(gate.kind === "ambiguous" ? gate.links : []).map((l) => (
                <option
                  key={`${l.jd_org_id}:${l.jd_field_id}`}
                  value={`${l.jd_org_id}:${l.jd_field_id}`}
                >
                  {/*
                    the NAME when the best-effort read got one — no user knows their JD fields
                    by numeric id, and this pick is the only safeguard on a combined field
                  */}
                  {jdNames[`${l.jd_org_id}:${l.jd_field_id}`] ??
                    `JD field ${l.jd_field_id} (org ${l.jd_org_id})`}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function JdSendGates({
  unlinked,
  ambiguous,
  chosen,
  jdNames,
  onLink,
  onChoose,
}: {
  unlinked: GatedField[];
  ambiguous: GatedField[];
  chosen: Record<string, string>;
  jdNames: Record<string, string>;
  onLink: (field: DashboardField) => void;
  onChoose: (fieldId: string, value: string) => void;
}) {
  return (
    <>
      <UnlinkedFields unlinked={unlinked} onLink={onLink} />
      <AmbiguousFields
        ambiguous={ambiguous}
        chosen={chosen}
        jdNames={jdNames}
        onChoose={onChoose}
      />
    </>
  );
}
